import { useQuery } from '@tanstack/react-query';

import { EnvironmentCountries, FunctionalError } from '@e-origin/shared';

import { useDeclarant } from '@hooks/declarant';
import { showErrorToast } from '@hooks/showErrorToast';
import { useSettings } from '@hooks/useSettings';
import { apiClient } from '@utils';

export const getDeclarationsCustomsCodes = async (
  families: string[],
  values: string[],
  locale = 'en',
  system: string = undefined,
): Promise<{ family: string; values: { value: string; label: string }[] }[]> => {
  try {
    const response = await apiClient.multilingualCode.getDeclarationsCustomsCodes.mutation({
      body: { locale: locale.toLowerCase(), families, values, system },
    });
    if (response.status !== 200) throw response.body;

    return response.body.codes as { family: string; values: { value: string; label: string }[] }[];
  } catch (error) {
    showErrorToast(error, 'Error fetching the codes!');
    return [];
  }
};

export const useDeclarationsErrorDescriptions = (functionalErrors: FunctionalError[]) => {
  const { data: declarant } = useDeclarant();
  const { data: settings } = useSettings();

  const shouldFetchErrorTexts = functionalErrors.length > 0 && settings?.country === EnvironmentCountries.NL;
  const errorReasons = functionalErrors.map((e) => e.errorReason);

  return useQuery({
    queryKey: ['declarations-error-descriptions', errorReasons, declarant?.language],
    queryFn: async () => {
      const families = ['ErrorCodesSystemDescription', 'ErrorCodesDeclaDescription'];
      const result = await getDeclarationsCustomsCodes(families, errorReasons, declarant?.language);

      const newTexts: Record<number, { system: string; declaration: string }> = {};

      functionalErrors.forEach((error) => {
        const systemFamily = result.find((fam) => fam.family === 'ErrorCodesSystemDescription');
        const declaFamily = result.find((fam) => fam.family === 'ErrorCodesDeclaDescription');

        const systemResult = systemFamily?.values.find((item) => item.value === error.errorReason);
        const declaResult = declaFamily?.values.find((item) => item.value === error.errorReason);

        newTexts[error.sequenceNumber] = {
          system: systemResult ? systemResult.label : '',
          declaration: declaResult ? declaResult.label : '',
        };
      });

      return newTexts;
    },
    enabled: shouldFetchErrorTexts,
    staleTime: Infinity,
  });
};
